import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { ButtonAdd, TextEditor } from "@neonumy/ant-design";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { getFormError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import useCommentsApi from "../hooks/useCommentsApi";
import React from "react";

interface CreateUpdateCommentFormValues {
  name: string;
  reference: string;
  description: string;
  [key: string]: unknown;
}

interface CreateUpdateCommentFormPropsTypes {
  refetch: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<ApolloQueryResult<ListCommentsResponse>>;
  id?: ID;
}

export default function CreateUpdateComment(
  props: CreateUpdateCommentFormPropsTypes,
): React.ReactElement {
  const { id, refetch } = props;
  const urlParams: RouteParams = useParams();
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const { createUpdateComment, getComment } = useCommentsApi(id, form);
  const { notificationError, notificationSuccessfullySaved } = useNotification();

  useEffect(() => {
    if (id && isOpen) {
      getComment({
        variables: {
          id,
        },
      });
    }
  }, [id, isOpen]);

  const onFinish = async (values: CreateUpdateCommentFormValues) => {
    let errors: ErrorType[] | undefined = [];
    const updateId = id ? { id } : {};

    const variables = {
      input: {
        companyId: urlParams.companyId,
        name: values?.name,
        reference: values?.reference,
        description: values?.description,
        ...updateId,
      },
    };

    const response = await createUpdateComment({ variables });

    errors = id ? response?.data?.updateComment?.errors : response?.data?.createComment?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved(t("Comment"));
      form.resetFields();
      setIsOpen(false);
    }
  };

  const showModal = () => {
    setIsOpen(true);
  };

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <>
      {id ? (
        <TooltipButton placement="top" handleOnClick={showModal} icon={<MdEdit />} />
      ) : (
        <Row justify="end" className="margin-top">
          <ButtonAdd onClick={showModal}>{t("Add")}</ButtonAdd>
        </Row>
      )}
      <Modal
        title={id ? t("Edit comment") : t("New comment")}
        open={isOpen}
        onOk={handleOk}
        okText={t("Save")}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        onCancel={handleCancel}
        width={"50%"}
        className="antd-modal"
      >
        <Form
          name="createUpdateComment"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t("Name")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="reference"
                label={t("Reference")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="description"
                label={t("Description")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <TextEditor />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
